'use client';

import {
  createContext,
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import {AdFoxScriptState, AdFoxStatsEvent} from '@shared/Ads/Yandex/types';

type YandexContext = {
  adFoxStatsEvents: {
    get: () => AdFoxStatsEvent[];
    set: (item: AdFoxStatsEvent) => void;
  };
  adFoxState: {
    adFoxHasAds: boolean;
    setAdFoxAds: Dispatch<SetStateAction<boolean>>;
  };
  adFoxScriptState: {
    state: AdFoxScriptState;
    setState: Dispatch<SetStateAction<AdFoxScriptState>>;
  };
};

type ProviderProps = {
  children: React.ReactNode;
};

export const YandexContext = createContext<YandexContext>({
  adFoxStatsEvents: {
    get: () => [],
    set: () => null,
  },
  adFoxScriptState: {
    setState: () => null,
    state: 'not_initialized',
  },
  adFoxState: {
    adFoxHasAds: false,
    setAdFoxAds: () => null,
  },
});

export const YandexProvider = ({children}: ProviderProps) => {
  const adFoxStatsEvents = useRef<AdFoxStatsEvent[]>([]);
  const [adFoxScriptState, setAdFoxScriptState] =
    useState<AdFoxScriptState>('not_initialized');
  const [adFoxState, setAdFoxState] = useState(false);
  const store = {
    adFoxStatsEvents: {
      get: useCallback(() => {
        const events = [...adFoxStatsEvents.current];
        adFoxStatsEvents.current = [];
        return events;
      }, [adFoxStatsEvents]),
      set: useCallback(
        (item: AdFoxStatsEvent) => {
          adFoxStatsEvents.current = [...adFoxStatsEvents.current, item];
        },
        [adFoxStatsEvents],
      ),
    },
    adFoxScriptState: useMemo(
      () => ({
        state: adFoxScriptState,
        setState: setAdFoxScriptState,
      }),
      [adFoxScriptState, setAdFoxScriptState],
    ),
    adFoxState: useMemo(
      () => ({
        adFoxHasAds: adFoxState,
        setAdFoxAds: setAdFoxState,
      }),
      [adFoxState, setAdFoxState],
    ),
  };

  return (
    <YandexContext.Provider value={store}>{children}</YandexContext.Provider>
  );
};
